import {useMemo, useEffect} from 'react';
import type {
  Collection as ReactAriaCollection,
  Key,
  Node,
  SingleSelection,
} from '@react-types/shared';
import type {
  MultipleSelectionStateProps,
  SingleSelectListState,
} from '@sail/react-aria';
import {
  SelectionManager,
  useMultipleSelectionState,
  useControlledState,
  ReactAriaListCollection as ListCollection,
} from '@sail/react-aria';

import type {Collection, Item} from './hooks';

function getItemNode<T>(item: Item<T>, index: number): Node<T> {
  return {
    type: 'item',
    props: item.data,
    rendered: null,
    textValue: item.typeaheadValue || '',
    'aria-label': item['aria-label'],
    hasChildNodes: false,
    childNodes: [],

    key: item.key,
    parentKey: undefined,
    level: 0,
    index,
    value: item.data,
  };
}

export function toReactAriaCollection<T>(
  getItems: Collection<T>['getItems'],
  filter?: (items: Array<Item<T>>) => Array<Item<T>>,
): Array<Node<T>> {
  const items = filter ? filter(getItems()) : getItems();
  return items.map((item: Item<T>, i: number) => getItemNode(item, i));
}

export interface ListState<T> {
  /** A collection of items in the list. */
  collection: ReactAriaCollection<Node<T>>;

  /** A set of items that are disabled. */
  disabledKeys: Set<Key>;

  /** A selection manager to read and update multiple selection state. */
  selectionManager: SelectionManager;
}

/**
 * A replacement for
 * [useListState](https://react-spectrum.adobe.com/react-stately/useListState.html)
 * from react-stately. Pass `getItems` from `useCollection` in here to produce a
 * list of items.
 */
export function useListState<T>({
  getItems,
  filter,
  disabledKeys,
  selection,
}: {
  getItems: Collection<T>['getItems'];
  filter?: (items: Array<Item<T>>) => Array<Item<T>>;
  disabledKeys?: Set<Key>;
  selection?: MultipleSelectionStateProps;
}): ListState<T> {
  const selectionState = useMultipleSelectionState(selection || {});
  const disabledKeysSet = useMemo(
    () => (disabledKeys ? new Set(disabledKeys) : new Set<Key>()),
    [disabledKeys],
  );

  const collection = useMemo(
    () => new ListCollection(toReactAriaCollection(getItems, filter)),
    [getItems, filter],
  );

  // Reset focused key if that item is deleted from the collection.
  useEffect(() => {
    if (
      selectionState.focusedKey != null &&
      !collection.getItem(selectionState.focusedKey)
    ) {
      selectionState.setFocusedKey(null);
    }
    // This is copied from react-aria. We cannot add `selectionState.setFocusedKey`
    // here as it's a new function each render. All that function does it set refs
    // so this is safe, but we must disabled this rule because of react-aria's code
    //
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
  }, [collection, selectionState.focusedKey]);

  return {
    collection,
    disabledKeys: disabledKeysSet,
    selectionManager: new SelectionManager(collection, selectionState),
  };
}

/**
 * A replacement for
 * [useSingleSelectListState](https://react-spectrum.adobe.com/react-stately/useSingleSelectListState.html)
 * Provides state management for list-like components with single selection.
 * Handles building a collection of items from props, and manages selection state.
 */
export function useSingleSelectListState<T>({
  getItems,
  filter,
  disabledKeys,
  selection,
}: {
  getItems: Collection<T>['getItems'];
  filter?: (items: Array<Item<T>>) => Array<Item<T>>;
  disabledKeys?: Set<Key>;
  selection: SingleSelection;
}): SingleSelectListState<T> {
  const {
    selectedKey: value,
    defaultSelectedKey,
    onSelectionChange,
  } = selection as Required<SingleSelection>;

  const [selectedKey, setSelectedKey] = useControlledState(
    value as Key,
    defaultSelectedKey ?? null,
    onSelectionChange,
  );
  const selectedKeys = useMemo(
    () => (selectedKey != null ? [selectedKey] : []),
    [selectedKey],
  );
  const {
    collection,
    disabledKeys: disabledKeysOut,
    selectionManager,
  } = useListState({
    getItems,
    filter,
    disabledKeys,
    selection: {
      selectionMode: 'single',
      disallowEmptySelection: true,
      allowDuplicateSelectionEvents: true,
      selectedKeys,
      onSelectionChange: (keys) => {
        const key = (keys as Set<Key>).values().next().value;

        // Always fire onSelectionChange, even if the key is the same
        // as the current key (useControlledState does not).
        if (key === selectedKey && onSelectionChange) {
          onSelectionChange(key);
        }

        if (key) {
          setSelectedKey(key.toString());
        }
      },
    },
  });

  const selectedItem = collection.getItem(selectedKey) as Node<T>;

  return {
    collection,
    disabledKeys: disabledKeysOut,
    selectionManager,
    selectedKey,
    setSelectedKey,
    selectedItem,
  };
}
